import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Printer, Store, RotateCcw, Mail, PhoneCall } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const CRITERIA = [
    { key: "accueil", label: "Accueil / enveloppement" },
    { key: "soutien", label: "Soutien" },
    { key: "aeration", label: "Aération" },
    { key: "independance", label: "Indépendance de couchage" },
    { key: "mouvement", label: "Facilité de mouvement" },
];

// Server-authoritative; frontend never recomputes rounding.
const TIER_STYLES = {
    top:   { badge: "bg-[#2A84C8] text-white",     text: "text-[#2A84C8]" },
    good:  { badge: "bg-[#2A84C8]/10 text-[#2A84C8] border border-[#2A84C8]/30", text: "text-[#2A84C8]" },
    try:   { badge: "bg-amber-100 text-amber-800 border border-amber-200",       text: "text-amber-800" },
    lower: { badge: "bg-brand-beige text-brand-ink/70",                          text: "text-brand-ink/60" },
};

const SCORE_DISCLAIMER =
    "Ce pourcentage est une aide à la décision calculée à partir de vos réponses et des caractéristiques techniques du matelas. Il ne constitue ni une garantie de confort ni un avis médical.";

const FINAL_DISCLAIMER =
    "Ce diagnostic constitue une première orientation. Votre ressenti lors de l'essai en boutique reste essentiel pour confirmer le confort qui vous convient.";

export default function Result() {
    const { id } = useParams();
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [showAppointment, setShowAppointment] = useState(false);

    useEffect(() => {
        api.get(`/questionnaires/${id}`)
            .then((r) => setData(r.data))
            .catch((e) => setError(formatApiError(e)));
    }, [id]);

    if (error) {
        return <ResultErrorScreen message={error} />;
    }
    if (!data) {
        return <div className="min-h-screen flex items-center justify-center text-brand-ink/60">Analyse de votre profil…</div>;
    }

    const { profile, contact } = data;
    const details = data.recommendation_details || {};
    const primary = details.primary;
    const alternative = details.alternative;
    const hr3Plus = details.hr3_plus_card;
    const coupleDiv = details.couple_divergence;
    const personalized = details.personalized_reasons || [];
    const tips = details.boutique_tips || [];
    const answers = data.answers || {};
    const otherTexts = answers.other_texts || {};
    const dimensionCustom = answers.dimension_custom;
    const scores = profile.scores;
    const perSleeper = profile.per_sleeper_needs || [];
    const visibleCriteria = profile.is_couple ? CRITERIA : CRITERIA.filter((c) => c.key !== "independance");

    // Phase 6: budget-vs-primary-price warning. Never shown when the client
    // opted for "privilégier mon confort". Never shown if no catalog price exists.
    const requestedBudget = Number(answers.budget) || 0;
    const budgetPriorityConfort = !!answers.budget_priority_confort;
    const primaryPrice = primary && typeof primary.price === "number" ? primary.price : null;
    const budgetInsufficient = !budgetPriorityConfort && requestedBudget > 0 && primaryPrice != null && primaryPrice > requestedBudget;

    // When HR3 Plus is triggered, standard picks become "Alternatives à comparer".
    const showHR3Plus = !!hr3Plus;
    const stdBlockTitle = showHR3Plus ? "Alternatives à comparer" : "Votre résultat";

    return (
        <div className="min-h-screen bg-brand-beige print:bg-white pb-16" data-testid="result-page">
            <div className="max-w-5xl mx-auto px-6 py-10 md:py-14">
                {/* Top bar */}
                <div className="flex justify-between items-center mb-8 print:hidden">
                    <Link to="/" className="text-sm text-brand-ink/60 hover:text-[#2A84C8]" data-testid="result-home-link">
                        ← Retour à l'accueil
                    </Link>
                    <button onClick={() => window.print()} className="ghost-btn" data-testid="print-btn">
                        <Printer className="w-4 h-4" /> Imprimer / PDF
                    </button>
                </div>

                {/* 1 — Votre profil de confort */}
                <motion.section initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} data-testid="section-profile">
                    <p className="text-sm uppercase tracking-widest text-[#2A84C8] font-medium mb-3">
                        Votre profil de confort
                    </p>
                    <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-semibold text-brand-ink mb-3">
                        {profile.profile_label}
                    </h1>
                    <p className="text-base text-brand-ink/70 max-w-2xl">
                        Voici la synthèse de vos réponses. Elle sert de repère pour orienter votre essai en boutique.
                    </p>

                    <div className="mt-8 bg-white rounded-3xl p-6 md:p-8 shadow-sm border border-brand-ink/5">
                        <h2 className="font-heading text-base md:text-lg font-semibold text-brand-ink mb-6">Vos besoins dominants</h2>
                        <div className="space-y-5" data-testid="profile-scores">
                            {visibleCriteria.map((c) => {
                                const s = Math.max(0, Math.min(100, Number(scores?.[c.key] ?? 0)));
                                return (
                                    <div key={c.key} data-testid={`score-${c.key}`}>
                                        <div className="flex justify-between text-sm mb-2">
                                            <span className="text-brand-ink font-medium">{c.label}</span>
                                            <span className="text-[#2A84C8] font-semibold tabular-nums">{s} %</span>
                                        </div>
                                        <div className="h-3 bg-brand-beige rounded-full overflow-hidden">
                                            <motion.div
                                                initial={{ width: 0 }}
                                                animate={{ width: `${s}%` }}
                                                transition={{ duration: 0.8, ease: "easeOut" }}
                                                className="h-full bg-[#2A84C8]"
                                            />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Couple: two dominant-needs blocks + divergence indicator */}
                    {profile.is_couple && (perSleeper.length >= 2 || coupleDiv) && (
                        <div className="mt-6 grid md:grid-cols-2 gap-4" data-testid="couple-blocks">
                            {perSleeper.slice(0, 2).map((axes, i) => (
                                <div key={i} className="bg-white rounded-2xl p-5 border border-brand-ink/10" data-testid={`profile-dormeur-${i + 1}`}>
                                    <h3 className="font-heading text-base font-semibold text-brand-ink mb-3">Profil du dormeur {i + 1}</h3>
                                    <ul className="text-sm text-brand-ink/70 space-y-1">
                                        <li>Accueil : <b className="text-brand-ink">{Math.round(axes.accueil || 0)} %</b></li>
                                        <li>Soutien : <b className="text-brand-ink">{Math.round(axes.soutien || 0)} %</b></li>
                                        <li>Aération : <b className="text-brand-ink">{Math.round(axes.aeration || 0)} %</b></li>
                                        <li>Facilité de mouvement : <b className="text-brand-ink">{Math.round(axes.mouvement || 0)} %</b></li>
                                    </ul>
                                </div>
                            ))}
                            {coupleDiv && (
                                <div className="md:col-span-2 bg-white rounded-2xl p-5 border border-[#2A84C8]/20" data-testid="couple-divergence">
                                    <p className="text-sm uppercase tracking-widest text-[#2A84C8] font-medium mb-1">Indicateur couple</p>
                                    <p className="font-heading text-base font-semibold text-brand-ink" data-testid="couple-divergence-label">{coupleDiv.label}</p>
                                    <p className="text-sm text-brand-ink/70 mt-1">{coupleDiv.hint}</p>
                                </div>
                            )}
                        </div>
                    )}
                </motion.section>

                {/* Explanation of thresholds */}
                <p className="mt-8 text-sm text-brand-ink/60 bg-white/60 border border-brand-ink/10 rounded-2xl p-4 max-w-2xl" data-testid="threshold-explanation">
                    À partir de 70 %, le matelas présente une bonne adéquation avec votre profil. À partir de 80 %,
                    l'adéquation est très forte. Un essai en boutique reste recommandé pour confirmer votre ressenti.
                </p>

                {/* 2 — Votre résultat */}
                <section className="mt-10" data-testid="section-recommendation">
                    <h2 className="font-heading text-2xl md:text-3xl font-semibold text-brand-ink mb-6">
                        {showHR3Plus ? "Solution recommandée pour votre couple" : stdBlockTitle}
                    </h2>

                    {showHR3Plus && (
                        <Hr3PlusCard card={hr3Plus} />
                    )}

                    {!showHR3Plus && primary && (
                        <div className="grid md:grid-cols-2 gap-6">
                            <MattressCard mattress={primary} testid="primary-mattress" priorityLabel="À essayer en priorité" isCouple={profile.is_couple} budgetInsufficient={budgetInsufficient} />
                            {alternative && (
                                <MattressCard mattress={alternative} testid="alt-mattress" priorityLabel="Alternative à comparer" isCouple={profile.is_couple} secondary />
                            )}
                        </div>
                    )}

                    {showHR3Plus && (primary || alternative) && (
                        <>
                            <h3 className="font-heading text-lg font-semibold text-brand-ink mt-10 mb-4" data-testid="alternatives-title">Alternatives à comparer</h3>
                            <div className="grid md:grid-cols-2 gap-6">
                                {primary && <MattressCard mattress={primary} testid="primary-mattress" priorityLabel="Alternative à comparer" isCouple={profile.is_couple} secondary />}
                                {alternative && <MattressCard mattress={alternative} testid="alt-mattress" priorityLabel="Alternative à comparer" isCouple={profile.is_couple} secondary />}
                            </div>
                        </>
                    )}
                </section>

                {/* Comparative table between the two standard picks */}
                {primary && alternative && (
                    <section className="mt-10" data-testid="section-compare">
                        <h2 className="font-heading text-xl md:text-2xl font-semibold text-brand-ink mb-4">Comparer ces deux modèles</h2>
                        <CompareTable a={primary} b={alternative} />
                    </section>
                )}

                {/* Personalized "why this choice" reasons */}
                {personalized.length > 0 && (
                    <section className="mt-10 bg-white rounded-3xl p-6 md:p-8 shadow-sm border border-brand-ink/5" data-testid="section-why">
                        <h3 className="font-heading text-xl font-semibold text-brand-ink mb-4">
                            Pourquoi ce choix vous correspond
                        </h3>
                        <ul className="space-y-2 text-brand-ink/80" data-testid="reasons-list">
                            {personalized.map((r, i) => (
                                <li key={i} className="flex gap-3">
                                    <span className="text-[#2A84C8] mt-1">•</span>
                                    <span>{r}</span>
                                </li>
                            ))}
                        </ul>
                    </section>
                )}

                {/* Précisions "Autre" & dimension personnalisée */}
                {(otherTexts.change_reason || (otherTexts.discomfort && Object.keys(otherTexts.discomfort).length) || dimensionCustom) && (
                    <section className="mt-6 bg-white rounded-3xl p-6 md:p-8 shadow-sm border border-brand-ink/5" data-testid="section-precisions">
                        <h3 className="font-heading text-lg font-semibold text-brand-ink mb-3">Vos précisions</h3>
                        <ul className="text-sm text-brand-ink/70 space-y-1">
                            {otherTexts.change_reason && (
                                <li data-testid="precision-change-reason"><b className="text-brand-ink">Raison du changement :</b> {otherTexts.change_reason}</li>
                            )}
                            {otherTexts.discomfort && Object.entries(otherTexts.discomfort).map(([k, v]) => (
                                <li key={k} data-testid={`precision-discomfort-${k}`}><b className="text-brand-ink">Gêne précisée (dormeur {Number(k) + 1}) :</b> {v}</li>
                            ))}
                            {dimensionCustom && (
                                <li data-testid="precision-dimension-custom"><b className="text-brand-ink">Dimension sur-mesure :</b> {dimensionCustom.largeur} × {dimensionCustom.longueur} cm</li>
                            )}
                        </ul>
                    </section>
                )}

                {/* 3 — Conseils pour votre essai en boutique */}
                {tips.length > 0 && (
                    <section className="mt-10 bg-white rounded-3xl p-6 md:p-8 shadow-sm border border-brand-ink/5" data-testid="section-tips">
                        <h3 className="font-heading text-xl font-semibold text-brand-ink mb-4">Conseils pour votre essai en boutique</h3>
                        <ul className="space-y-2 text-brand-ink/80" data-testid="tips-list">
                            {tips.map((t, i) => (
                                <li key={i} className="flex gap-3">
                                    <span className="text-[#2A84C8] mt-1">•</span>
                                    <span>{t}</span>
                                </li>
                            ))}
                        </ul>
                    </section>
                )}

                {/* Actions finales */}
                <div className="mt-10 bg-[#2A84C8] rounded-3xl p-6 md:p-10 text-white print:hidden">
                    <p className="text-white/90 max-w-2xl mb-6">
                        Vous pouvez imprimer ce résultat, contacter la boutique ou réserver un créneau pour venir essayer.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3">
                        <button onClick={() => setShowAppointment(true)} className="min-h-[60px] px-8 rounded-full bg-white text-[#2A84C8] font-medium hover:bg-white/90 transition-colors inline-flex items-center justify-center gap-2" data-testid="appointment-btn">
                            <Store className="w-5 h-5" /> Essayer en boutique
                        </button>
                        <a href="tel:+33677548220" className="min-h-[60px] px-8 rounded-full bg-white/10 text-white border border-white/30 hover:bg-white/20 transition-colors inline-flex items-center justify-center gap-2" data-testid="call-shop-btn">
                            <PhoneCall className="w-5 h-5" /> 06 77 54 82 20
                        </a>
                        <Link to="/questionnaire" className="min-h-[60px] px-8 rounded-full bg-transparent border border-white/40 text-white hover:bg-white/10 transition-colors inline-flex items-center justify-center gap-2" data-testid="restart-btn">
                            <RotateCcw className="w-5 h-5" /> Recommencer
                        </Link>
                    </div>
                </div>

                <p className="mt-8 text-sm text-brand-ink/60 text-center max-w-3xl mx-auto" data-testid="final-disclaimer">
                    {FINAL_DISCLAIMER}
                </p>

                {showAppointment && (
                    <AppointmentModal questionnaireId={id} initial={contact} onClose={() => setShowAppointment(false)} />
                )}
            </div>
        </div>
    );
}

function MattressCard({ mattress, testid, priorityLabel, isCouple, secondary, budgetInsufficient }) {
    const adeq = mattress.adequation || { score: null, label: null, tier: null };
    const per = mattress.per_sleeper_adequation || {};
    const styles = TIER_STYLES[adeq.tier] || TIER_STYLES.lower;
    const price = mattress.price; // catalog-provided only; never invent
    return (
        <div className={`bg-white rounded-3xl p-6 shadow-sm border ${secondary ? "border-brand-ink/10" : "border-[#2A84C8]"}`} data-testid={testid}>
            <div className="flex items-start justify-between gap-3">
                <div>
                    <span className={`inline-block text-xs px-3 py-1 rounded-full mb-3 ${secondary ? "bg-brand-beige text-brand-ink" : "bg-[#2A84C8] text-white"}`} data-testid={`${testid}-label`}>
                        {priorityLabel}
                    </span>
                    <h3 className="font-heading text-2xl font-semibold text-brand-ink">{mattress.name}</h3>
                    <p className="text-sm text-brand-ink/50 mt-1">{mattress.range}</p>
                </div>
                {adeq.score != null && (
                    <div className="text-right shrink-0" data-testid={`${testid}-adequation`}>
                        <div className="text-3xl font-heading font-semibold text-[#2A84C8] tabular-nums" data-testid={`${testid}-score`}>{adeq.score} %</div>
                        <div className="text-xs text-brand-ink/60">score d'adéquation</div>
                    </div>
                )}
            </div>

            {adeq.label && (
                <div className="mt-3">
                    <span className={`inline-block text-xs px-3 py-1 rounded-full ${styles.badge}`} data-testid={`${testid}-tier`}>{adeq.label}</span>
                </div>
            )}

            {isCouple && (per.d1 || per.d2) && (
                <div className="flex flex-wrap gap-3 mt-3 text-xs text-brand-ink/60">
                    {per.d1?.score != null && <span data-testid={`${testid}-d1`}>Dormeur 1 : <b className="text-brand-ink">{per.d1.score}%</b></span>}
                    {per.d2?.score != null && <span data-testid={`${testid}-d2`}>Dormeur 2 : <b className="text-brand-ink">{per.d2.score}%</b></span>}
                </div>
            )}

            <p className="text-sm text-[#2A84C8] font-medium mt-3">{mattress.technology}</p>
            <p className="text-brand-ink/70 mt-3 text-sm leading-relaxed">{mattress.description}</p>

            <div className="mt-4 pt-4 border-t border-brand-ink/10 text-sm">
                {price != null ? (
                    <p className="text-brand-ink font-medium" data-testid={`${testid}-price`}>
                        Prix dans la dimension sélectionnée : {price} € TTC
                    </p>
                ) : (
                    <p className="text-brand-ink/60" data-testid={`${testid}-price-tbc`}>
                        Tarif à confirmer avec votre conseiller pour cette dimension.
                    </p>
                )}
                {budgetInsufficient && price != null && (
                    <p className="mt-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2" data-testid={`${testid}-budget-warning`}>
                        Le modèle qui correspond le mieux à votre profil peut dépasser le budget indiqué. Votre conseiller pourra
                        vous présenter les solutions les plus proches de vos besoins et vous aider à définir vos priorités.
                    </p>
                )}
            </div>

            <p className="mt-3 text-xs text-brand-ink/50 leading-relaxed" data-testid={`${testid}-score-disclaimer`}>
                {SCORE_DISCLAIMER}
            </p>
            <p className="mt-2 text-xs text-brand-ink/50">Ce matelas correspond à votre profil selon les réponses fournies.</p>
        </div>
    );
}

function Hr3PlusCard({ card }) {
    if (!card) return null;
    return (
        <div className="bg-white rounded-3xl p-6 md:p-8 border-2 border-[#2A84C8] shadow-sm" data-testid="hr3-plus-card">
            <span className="inline-block text-xs px-3 py-1 rounded-full bg-[#2A84C8] text-white mb-3">Solution couple</span>
            <h3 className="font-heading text-2xl font-semibold text-brand-ink mb-3" data-testid="hr3-plus-title">{card.title}</h3>
            <p className="text-brand-ink/70 leading-relaxed" data-testid="hr3-plus-description">{card.description}</p>
            <ul className="mt-4 space-y-1 text-sm text-brand-ink/80" data-testid="hr3-plus-features">
                {(card.features || []).map((f, i) => (
                    <li key={i} className="flex gap-2"><span className="text-[#2A84C8]">›</span>{f}</li>
                ))}
            </ul>
            {(card.hr3_plus_reasons || []).length > 0 && (
                <div className="mt-4 pt-4 border-t border-brand-ink/10">
                    <p className="text-xs uppercase tracking-widest text-[#2A84C8] mb-1">Motivation</p>
                    {card.hr3_plus_reasons.map((r, i) => (
                        <p key={i} className="text-sm text-brand-ink/70">{r}</p>
                    ))}
                </div>
            )}
            <p className="mt-4 text-xs text-brand-ink/50">Cette solution est proposée sans score d'adéquation chiffré : elle repose sur la combinaison de deux blocs HR3 indépendants. Un essai en boutique est recommandé.</p>
        </div>
    );
}

function CompareTable({ a, b }) {
    const attrs = (m) => m.attributes || {};
    const rows = [
        { label: "Sensation d'accueil", get: (m) => `${attrs(m).accueil ?? "—"}/100` },
        { label: "Niveau de soutien",   get: (m) => `${attrs(m).soutien ?? "—"}/100` },
        { label: "Aération",            get: (m) => `${attrs(m).aeration ?? "—"}/100` },
        { label: "Indépendance de couchage", get: (m) => `${attrs(m).independance ?? "—"}/100` },
        { label: "Facilité de mouvement",    get: (m) => `${attrs(m).mouvement ?? "—"}/100` },
        { label: "Adaptation aux positions", get: (m) => m.range || "—" },
        { label: "Caractéristiques principales", get: (m) => (m.features || []).slice(0, 3).join(" · ") || "—" },
    ];
    return (
        <div className="bg-white rounded-3xl border border-brand-ink/10 overflow-hidden" data-testid="compare-table">
            {/* Desktop table */}
            <table className="hidden md:table w-full text-sm">
                <thead className="bg-brand-beige/60 text-brand-ink">
                    <tr>
                        <th className="text-left px-4 py-3">Critère</th>
                        <th className="text-left px-4 py-3">{a.name}</th>
                        <th className="text-left px-4 py-3">{b.name}</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((r, i) => (
                        <tr key={i} className={i % 2 ? "bg-white" : "bg-brand-beige/20"} data-testid={`compare-row-${i}`}>
                            <td className="px-4 py-3 text-brand-ink/70">{r.label}</td>
                            <td className="px-4 py-3 text-brand-ink font-medium">{r.get(a)}</td>
                            <td className="px-4 py-3 text-brand-ink font-medium">{r.get(b)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
            {/* Mobile: stacked layout */}
            <div className="md:hidden divide-y divide-brand-ink/10">
                {rows.map((r, i) => (
                    <div key={i} className="p-4" data-testid={`compare-row-mobile-${i}`}>
                        <p className="text-xs uppercase tracking-widest text-brand-ink/50 mb-2">{r.label}</p>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                            <div><span className="block text-brand-ink/50 text-xs">{a.name}</span><span className="text-brand-ink font-medium">{r.get(a)}</span></div>
                            <div><span className="block text-brand-ink/50 text-xs">{b.name}</span><span className="text-brand-ink font-medium">{r.get(b)}</span></div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function ResultErrorScreen({ message }) {
    return (
        <div className="min-h-screen flex items-center justify-center p-6 bg-brand-beige" data-testid="result-error-screen">
            <div className="max-w-md w-full bg-white rounded-3xl p-8 border border-brand-ink/10 text-center">
                <h1 className="font-heading text-2xl font-semibold text-brand-ink mb-3" data-testid="result-error-title">Ce résultat est introuvable</h1>
                <p className="text-sm text-brand-ink/70 mb-6" data-testid="result-error-message">
                    Le lien utilisé est peut-être incomplet ou le diagnostic n'est plus accessible. Vous pouvez recommencer le questionnaire ou contacter votre conseiller.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <Link to="/questionnaire" className="primary-btn justify-center" data-testid="result-error-restart">
                        Recommencer le diagnostic
                    </Link>
                    <a href="tel:+33677548220" className="ghost-btn justify-center" data-testid="result-error-call">
                        <PhoneCall className="w-4 h-4" /> Contacter mon conseiller
                    </a>
                </div>
                {message && <p className="mt-6 text-xs text-brand-ink/40">Référence technique : {message.slice(0, 80)}</p>}
            </div>
        </div>
    );
}

function AppointmentModal({ questionnaireId, initial, onClose }) {
    const [form, setForm] = useState({
        prenom: initial?.prenom || "",
        telephone: initial?.telephone || "",
        email: initial?.email || "",
        date_souhaitee: "",
        creneau: "a_definir",
        creneau_precision: "",
        message: "",
    });
    const [sending, setSending] = useState(false);
    const [confirmed, setConfirmed] = useState(false);
    // Phase 6: stable submission_id — regenerated only after a successful send.
    const submissionIdRef = useRef(cryptoRandomId());

    const trimmed = (form.prenom || "").trim();
    const phoneOk = /^(?:(?:\+33|0033)\s?[1-9](?:[\s.-]?\d{2}){4}|0[1-9](?:[\s.-]?\d{2}){4})$/.test((form.telephone || "").trim());
    const emailOk = !form.email || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((form.email || "").trim());
    const dateOk = !form.date_souhaitee || form.date_souhaitee >= new Date().toISOString().slice(0, 10);
    const canSubmit = !sending && trimmed.length > 0 && phoneOk && emailOk && dateOk;

    const submit = async (e) => {
        e.preventDefault();
        if (!canSubmit || sending) return;
        setSending(true);
        try {
            await api.post("/appointments", {
                submission_id: submissionIdRef.current,
                prenom: trimmed,
                telephone: form.telephone.trim(),
                email: form.email.trim() || null,
                date_souhaitee: form.date_souhaitee || null,
                creneau: form.creneau,
                creneau_precision: (form.creneau_precision || "").trim() || null,
                message: (form.message || "").trim() || null,
                questionnaire_id: questionnaireId,
            });
            setConfirmed(true);
        } catch (err) {
            const msg = formatApiError(err);
            toast.error(`Une erreur est survenue. ${msg} Vous pouvez réessayer, aucun doublon ne sera créé.`);
        } finally {
            setSending(false);
        }
    };

    if (confirmed) {
        return (
            <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="appointment-modal">
                <div className="bg-white rounded-3xl p-6 md:p-8 max-w-md w-full text-center">
                    <h3 className="font-heading text-2xl font-semibold text-brand-ink mb-3">Merci !</h3>
                    <p className="text-sm text-brand-ink/70 mb-6" data-testid="apt-confirmation">
                        Votre demande de rendez-vous a bien été enregistrée. Votre conseiller vous recontactera pour confirmer la date et le créneau.
                    </p>
                    <button onClick={onClose} className="primary-btn justify-center" data-testid="apt-close">Fermer</button>
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="appointment-modal">
            <div className="bg-white rounded-3xl p-6 md:p-8 max-w-md w-full max-h-[95vh] overflow-y-auto">
                <h3 className="font-heading text-2xl font-semibold text-brand-ink mb-1">Prendre rendez-vous en boutique</h3>
                <p className="text-sm text-brand-ink/60 mb-6">La Ciotat · Le Petit Matelassier</p>
                <form onSubmit={submit} className="space-y-3">
                    <Field label="Prénom *" value={form.prenom} onChange={(v) => setForm({ ...form, prenom: v })} required testid="apt-prenom" />
                    <div>
                        <Field label="Téléphone *" value={form.telephone} onChange={(v) => setForm({ ...form, telephone: v })} required testid="apt-tel" />
                        {form.telephone && !phoneOk && <p className="text-xs text-red-600 mt-1" data-testid="apt-tel-error">Format téléphone français attendu (ex : 06 12 34 56 78).</p>}
                    </div>
                    <div>
                        <Field label="Email (facultatif)" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="apt-email" />
                        {form.email && !emailOk && <p className="text-xs text-red-600 mt-1" data-testid="apt-email-error">Adresse email invalide.</p>}
                    </div>
                    <div>
                        <Field label="Date souhaitée" type="date" value={form.date_souhaitee} onChange={(v) => setForm({ ...form, date_souhaitee: v })} testid="apt-date" />
                        {form.date_souhaitee && !dateOk && <p className="text-xs text-red-600 mt-1" data-testid="apt-date-error">La date choisie est déjà passée.</p>}
                    </div>
                    <div>
                        <span className="block text-sm text-brand-ink/70 mb-1">Créneau</span>
                        <div className="grid grid-cols-3 gap-2">
                            {[
                                { v: "matin",      l: "Matin" },
                                { v: "apres_midi", l: "Après-midi" },
                                { v: "a_definir",  l: "À définir" },
                            ].map((c) => (
                                <button key={c.v} type="button" data-testid={`apt-creneau-${c.v}`}
                                    className={`min-h-[44px] rounded-xl border px-3 text-sm ${form.creneau === c.v ? "bg-[#2A84C8] text-white border-[#2A84C8]" : "bg-white border-brand-ink/15 text-brand-ink"}`}
                                    onClick={() => setForm({ ...form, creneau: c.v })}>
                                    {c.l}
                                </button>
                            ))}
                        </div>
                    </div>
                    <Field label="Précision créneau (facultatif)" value={form.creneau_precision} onChange={(v) => setForm({ ...form, creneau_precision: v })} testid="apt-creneau-precision" placeholder="ex : samedi matin, plutôt 10h" />
                    <label className="block">
                        <span className="block text-sm text-brand-ink/70 mb-1">Remarque (facultatif)</span>
                        <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={2} maxLength={1000}
                            className="w-full rounded-xl border border-brand-ink/15 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-[#2A84C8]/30 focus:border-[#2A84C8]"
                            data-testid="apt-message" />
                    </label>
                    <p className="text-xs text-brand-ink/60 leading-relaxed pt-1" data-testid="apt-privacy">
                        Vos coordonnées sont utilisées uniquement pour répondre à votre demande et préparer votre rendez-vous. Elles ne sont ni vendues ni transmises à des tiers à des fins commerciales.
                    </p>
                    <div className="flex gap-3 justify-end pt-2">
                        <button type="button" onClick={onClose} className="ghost-btn" data-testid="apt-cancel">Annuler</button>
                        <button type="submit" disabled={!canSubmit} className="primary-btn" data-testid="apt-submit">
                            <Mail className="w-4 h-4" /> {sending ? "Envoi…" : "Envoyer"}
                        </button>
                    </div>
                </form>
                <div className="mt-6 pt-6 border-t border-brand-ink/10 text-sm text-brand-ink/60 flex items-center gap-2">
                    <PhoneCall className="w-4 h-4 text-[#2A84C8]" />
                    Vous pouvez aussi appeler la boutique au{" "}
                    <a href="tel:+33677548220" className="text-[#2A84C8] font-medium hover:underline">06 77 54 82 20</a>.
                </div>
            </div>
        </div>
    );
}

// RFC4122-ish random id fallback (aligned with Questionnaire.jsx).
function cryptoRandomId() {
    try { if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID(); } catch { /* ignore */ }
    const rand = () => Math.random().toString(16).slice(2, 10);
    return `${rand()}-${rand()}-${rand()}-${rand()}`;
}

function Field({ label, value, onChange, type = "text", required, placeholder, testid }) {
    return (
        <label className="block">
            <span className="block text-sm text-brand-ink/70 mb-1">{label}</span>
            <input
                type={type}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                required={required}
                placeholder={placeholder}
                className="w-full min-h-[48px] rounded-xl border border-brand-ink/15 px-4 focus:outline-none focus:ring-2 focus:ring-[#2A84C8]/30 focus:border-[#2A84C8]"
                data-testid={testid}
            />
        </label>
    );
}
